import { useState, useEffect } from "react";

export default function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    // Check if running on the client side
    if (typeof window === "undefined") {
      return defaultValue;
    }
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue === null ? defaultValue : JSON.parse(storedValue);
    } catch (error) {
      console.error(`Error reading localStorage key “${key}”:`, error);
      return defaultValue;
    }
  });

  // Listen for changes to this key in other tabs
  useEffect(() => {
    const listener = (e) => {
      if (
        typeof window !== "undefined" &&
        e.storageArea === localStorage &&
        e.key === key
      ) {
        try {
          setValue(e.newValue ? JSON.parse(e.newValue) : defaultValue);
        } catch (error) {
          console.error(
            `Error parsing localStorage key “${key}” on storage event:`,
            error,
          );
          setValue(defaultValue);
        }
      }
    };
    window.addEventListener("storage", listener);

    return () => {
      window.removeEventListener("storage", listener);
    };
  }, [key, defaultValue]);

  // Update localStorage when the state changes
  const setValueInLocalStorage = (newValue) => {
    setValue((currentValue) => {
      try {
        const result =
          typeof newValue === "function" ? newValue(currentValue) : newValue;
        if (typeof window !== "undefined") {
          // Check data size before storing
          const dataStr = JSON.stringify(result);
          const dataSizeKB = new Blob([dataStr]).size / 1024;
          
          // Skip localStorage for large data (> 4MB)
          if (dataSizeKB > 4096) {
            console.warn(`Data for key "${key}" is ${dataSizeKB.toFixed(2)}KB, too large for localStorage. Storing in memory only.`);
            return result;
          }
          
          // Try to clear space if needed
          try {
            localStorage.setItem(key, dataStr);
          } catch (quotaError) {
            if (quotaError.name === 'QuotaExceededError') {
              console.warn(`localStorage quota exceeded for key "${key}". Attempting to clear old data...`);
              
              // Clear other less important data first
              const keysToCheck = ['isco_data', 'isco_clusterGroupingsFeedback', 'isco_directionsFeedback', 'isco_customerStopsFeedback'];
              for (const oldKey of keysToCheck) {
                if (oldKey !== key && localStorage.getItem(oldKey)) {
                  localStorage.removeItem(oldKey);
                  console.log(`Cleared ${oldKey} to make space`);
                }
              }
              
              // Try again after clearing
              try {
                localStorage.setItem(key, dataStr);
              } catch (retryError) {
                console.error(`Still unable to store data for key "${key}" after clearing space. Storing in memory only.`);
              }
            }
          }
        }
        return result;
      } catch (error) {
        console.error(`Error setting localStorage key “${key}”:`, error);
        return currentValue; // Return current value if error occurs
      }
    });
  };

  return [value, setValueInLocalStorage];
}
